import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { CloudService, CognitoUserPool, ServiceCardData } from '../../core/services/cloud.service';
import { ToastService } from '../../core/services/toast.service';
import {
  CloudFilters,
  FacetDef,
  SortOption,
  parseFiltersFromParams,
  filtersToParams,
} from './cloud-filter.model';
import { MetricColumn } from './cloud-metrics-panel.component';
import { MonitoringService } from '../../core/services/monitoring.service';
import { ActivityItem, findingsToActivity, sortActivity } from './cloud-activity.model';

type Tab = 'overview' | 'userPools' | 'activity';

const HISTORY_METRICS: MetricColumn[] = [
  { key: 'signInThr',  label: 'Sign-in' },
  { key: 'refreshThr', label: 'Refresh' },
  { key: 'signUpThr',  label: 'Sign-up' },
];

const DEFAULT_SORT = 'name-asc';
const FACET_KEYS   = ['monitoring'];

const SORT_OPTIONS: SortOption[] = [
  { value: 'name-asc',        label: 'Name A → Z'      },
  { value: 'name-desc',       label: 'Name Z → A'      },
  { value: 'monitored-first', label: 'Monitored first' },
];

const FACETS: FacetDef[] = [
  {
    key: 'monitoring',
    label: 'Monitoring',
    options: [
      { value: 'monitored',   label: 'Monitored'     },
      { value: 'unmonitored', label: 'Not monitored' },
    ],
  },
];

/**
 * Cognito monitoring is at the user-pool level (CloudWatch UserPool dimension).
 * v1 detects request throttling; Cognito does not publish a native sign-in
 * *failure* metric in CloudWatch, so failed-auth alerting is not yet available.
 * The monitored resource id is the user pool id.
 */
@Component({
  selector: 'app-cognito-detail',
  templateUrl: './cognito-detail.component.html',
})
export class CognitoDetailComponent implements OnInit, OnDestroy {
  selectedTab: Tab = 'overview';
  userPools: CognitoUserPool[] = [];
  loading = true;
  loadError: string | null = null;

  filters: CloudFilters = { query: '', facets: {}, sort: DEFAULT_SORT };

  readonly tabs: { id: Tab; label: string }[] = [
    { id: 'overview',  label: 'Overview'   },
    { id: 'userPools', label: 'User Pools' },
    { id: 'activity',  label: 'Activity'   },
  ];

  readonly facets      = FACETS;
  readonly sortOptions = SORT_OPTIONS;

  readonly serviceCard: ServiceCardData = { id: 'cognito', name: 'Cognito', icon: 'users', route: '/cloud/cognito', status: 'healthy', metrics: [] };
  readonly historyMetrics = HISTORY_METRICS;

  drawerItemId: string | null = null;
  selectedId: string | null = null;

  activity: ActivityItem[] = [];
  activityLoading = true;
  refreshing = false;

  private sub?: Subscription;
  private routeSub?: Subscription;
  private findingsSub?: Subscription;

  constructor(
    private readonly cloud: CloudService,
    private readonly toast: ToastService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly monitoring: MonitoringService,
  ) {}

  ngOnInit(): void {
    this.cloud.loadItemPoliciesForService('cognito').subscribe();
    this.routeSub = this.route.queryParams.subscribe(params => {
      const p = params as Record<string, string>;
      this.filters = parseFiltersFromParams(p, DEFAULT_SORT, FACET_KEYS);
      this.selectedId = p['r'] ?? null;
    });
    this.loadData(false);
  }

  /** Loads the user-pool list (cache-aware), monitoring state (monitored count), and activity findings. */
  private loadData(bypass: boolean): void {
    this.sub?.unsubscribe();
    this.sub = this.cloud.getCognitoUserPoolsResult(bypass).subscribe(({ userPools, error }) => {
      this.userPools  = userPools;
      this.loadError  = userPools.length === 0 ? error : null;
      this.loading    = false;
      this.refreshing = false;
    });
    this.cloud.loadMonitoringState().subscribe();
    this.findingsSub?.unsubscribe();
    this.findingsSub = this.monitoring.getFindings().subscribe(findings => {
      this.activityLoading = false;
      this.activity        = sortActivity(findingsToActivity(findings, 'COGNITO'));
    });
  }

  /** Manual refresh — bypass the cache and reload (guarded against double-clicks). */
  refresh(): void {
    if (this.refreshing) return;
    this.refreshing = true;
    this.loadData(true);
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.routeSub?.unsubscribe();
    this.findingsSub?.unsubscribe();
  }

  onActivitySelect(item: ActivityItem): void {
    this.selectedTab = 'userPools';
    this.selectResource(item.resourceId);
  }

  // ── Overview counts ─────────────────────────────────────────────────────────

  get totalCount():     number { return this.userPools.length; }
  get monitoredCount(): number { return this.userPools.filter(p => this.isMonitored(p.id)).length; }

  // ── Filtered + sorted list ──────────────────────────────────────────────────

  get filteredPools(): CognitoUserPool[] {
    let result = this.userPools;

    const q = this.filters.query.toLowerCase().trim();
    if (q) result = result.filter(p =>
      p.name.toLowerCase().includes(q) ||
      p.id.toLowerCase().includes(q),
    );

    const monitoring = this.filters.facets['monitoring'] ?? [];
    if (monitoring.length) {
      result = result.filter(p => {
        const mon = this.isMonitored(p.id);
        return (monitoring.includes('monitored') && mon) || (monitoring.includes('unmonitored') && !mon);
      });
    }

    return this.sorted(result, this.filters.sort);
  }

  private sorted(pools: CognitoUserPool[], sort: string): CognitoUserPool[] {
    return [...pools].sort((a, b) => {
      switch (sort) {
        case 'name-desc':       return b.name.localeCompare(a.name);
        case 'monitored-first': {
          const aM = this.isMonitored(a.id) ? 0 : 1;
          const bM = this.isMonitored(b.id) ? 0 : 1;
          return aM - bM || a.name.localeCompare(b.name);
        }
        default: return a.name.localeCompare(b.name);
      }
    });
  }

  onFiltersChange(f: CloudFilters): void {
    this.router.navigate([], {
      relativeTo:          this.route,
      queryParams:         filtersToParams(f, FACET_KEYS),
      queryParamsHandling: 'merge',
      replaceUrl:          true,
    });
  }

  // ── Monitoring ──────────────────────────────────────────────────────────────

  get quotaUsage() { return this.cloud.getQuotaUsage(); }
  get canMonitorMore(): boolean { return this.cloud.canEnableMoreMonitoring(); }

  isMonitored(userPoolId: string): boolean { return this.cloud.isMonitored('cognito', userPoolId); }

  toggleMonitoring(userPoolId: string): void {
    const current = this.cloud.isMonitored('cognito', userPoolId);
    if (!current && !this.cloud.canEnableMoreMonitoring()) {
      this.toast.show(this.cloud.getQuotaExceededMessage(), 'error');
      return;
    }
    this.cloud.toggleMonitoring('cognito', userPoolId, !current).subscribe({
      error: err => {
        if (err.status === 403) {
          const msg = err.error?.code === 'QUOTA_EXCEEDED'
            ? this.cloud.getQuotaExceededMessage()
            : 'Only org owners and admins can configure monitoring.';
          this.toast.show(msg, 'error');
        }
      },
    });
  }

  // ── Metric-history drill-down ───────────────────────────────────────────────

  selectResource(userPoolId: string): void {
    this.router.navigate([], { relativeTo: this.route, queryParams: { r: userPoolId }, queryParamsHandling: 'merge', replaceUrl: true });
  }
  closeResource(): void {
    this.router.navigate([], { relativeTo: this.route, queryParams: { r: null }, queryParamsHandling: 'merge', replaceUrl: true });
  }

  // ── Item settings drawer ────────────────────────────────────────────────────

  openItemSettings(userPoolId: string): void  { this.drawerItemId = userPoolId; }
  closeItemSettings(): void                    { this.drawerItemId = null; }

  itemOverrideSummary(userPoolId: string): string {
    return this.cloud.getItemOverrideSummary('cognito', userPoolId);
  }
}
